<template>
    <div>
        <BigButton
            task="startOrdering"
            class="bg-order-buttons font-extrabold text-white"
            :loading="isSubmitting"
            :disabled="props.disabled"
            @click="onStartOrderingClick"
        />

        <ActionStatus kind="startOrdering" :state="isSubmitting ? 'loading' : null" />

        <PhotobookLoginModal v-model:model-value="showLoginModal" @login-success="onLoginSuccess" @continue="startOrdering" />
        <PhotobookRecentProjectsModal v-model:model-value="showRecentProjectsModal" @start-new="startOrdering" />
        <PhotobookNewProjectModal v-model:model-value="showNewProjectModal" @start="onNewProjectStart" />
    </div>
</template>

<script setup lang="ts">
import BigButton from '@/components/buttons/BigButton.vue';
import { useToolPagePropsComposable } from '@/composables/tools/mediaclip/UseToolPagePropsComposable';
import PhotobookLoginModal from '@/pages/product-page/partials/product-info/partials/modals/PhotobookLoginModal.vue';
import PhotobookNewProjectModal from '@/pages/product-page/partials/product-info/partials/modals/PhotobookNewProjectModal.vue';
import PhotobookRecentProjectsModal from '@/pages/product-page/partials/product-info/partials/modals/PhotobookRecentProjectsModal.vue';
import ActionStatus from '@/components/status/ActionStatus.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { useMediaclipSessionStore } from '@/stores/tool/MediaclipSessionStore';
import type { AppPageProps } from '@/types';
import ProductPageSimpleController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/ProductPageSimpleController';
import { router, usePage } from '@inertiajs/vue3';
import { ref } from 'vue';

type ButtonProps = {
    disabled?: boolean;
};

const props = withDefaults(defineProps<ButtonProps>(), {
    disabled: false,
});

const productStore = useProductStore();
const productPageStore = useProductPageStore();
const page = usePage<AppPageProps>();

const isSubmitting = ref<boolean>(false);
const isCustomerAuthenticated = ref<boolean>(page.props.magento_customer_auth?.is_authenticated ?? false);
const showLoginModal = ref<boolean>(false);
const showRecentProjectsModal = ref<boolean>(false);
const showNewProjectModal = ref<boolean>(false);

function finalizeConfiguratorNavigation(): void {
    const { mediaclipProject } = useToolPagePropsComposable();

    if (!mediaclipProject.value) {
        return;
    }

    useMediaclipSessionStore().activate(mediaclipProject.value.mediaclip_project_id, productStore.selectedProduct.product.sku);
}

function onStartOrderingClick(): void {
    if (props.disabled) {
        return;
    }

    if (isMissingMediaclipUserText()) {
        productPageStore.showMediaclipUserTextError = true;
        return;
    }

    if (shouldShowLoginModal()) {
        // The page can come from the CDN cache, rendered for a guest: ask the server before asking for a login.
        reloadAuthentication(askLoginOrContinueOrdering);
        return;
    }

    continueOrdering();
}

function askLoginOrContinueOrdering(): void {
    if (shouldShowLoginModal()) {
        showLoginModal.value = true;
        return;
    }

    continueOrdering();
}

function onLoginSuccess(): void {
    reloadAuthentication(continueOrdering);
}

function reloadAuthentication(onReloaded: () => void): void {
    router.reload({
        only: ['magento_customer_auth', 'cart_count'],
        onSuccess: () => {
            isCustomerAuthenticated.value = page.props.magento_customer_auth?.is_authenticated ?? false;
            onReloaded();
        },
    });
}

function continueOrdering(): void {
    if (shouldShowRecentProjectsModal()) {
        showRecentProjectsModal.value = true;
        return;
    }

    startOrdering();
}

function mediaclipUserText(): string | undefined {
    return productPageStore.mediaclipUserText?.trim() || productStore.configurableProduct.mediaclip_user_text || undefined;
}

function isMissingMediaclipUserText(): boolean {
    return productStore.isMediaclipLetterCollageProduct && !mediaclipUserText();
}

function shouldShowLoginModal(): boolean {
    if (!productStore.isMediaclipBookProduct) {
        return false;
    }
    return !isCustomerAuthenticated.value;
}

function shouldShowRecentProjectsModal(): boolean {
    if (!productStore.isMediaclipBookProduct) {
        return false;
    }

    if (!isCustomerAuthenticated.value) {
        return false;
    }

    return productStore.configurableProduct.show_recent_projects_modal;
}

function startOrdering(): void {
    if (productStore.isMediaclipBookProduct) {
        showNewProjectModal.value = true;
        return;
    }

    submitProject();
}

function onNewProjectStart(title?: string): void {
    submitProject(title);
}

function submitProject(projectTitle?: string): void {
    if (isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;

    router.post(
        ProductPageSimpleController({
            configurableProduct: productStore.configurableProduct.url_key,
            product: productStore.selectedProduct.product.sku,
        }).url,
        {
            mediaclip_user_text: mediaclipUserText(),
            project_title: projectTitle,
        },
        {
            onSuccess: finalizeConfiguratorNavigation,
            onFinish: () => {
                isSubmitting.value = false;
            },
        },
    );
}
</script>
